import * as store from "../state/store.js";
import { parseDxf } from "../dxf/parser.js";
import { exportDxf as writeDxf } from "../dxf/exporter.js";
import { processDxf, resetModel } from "./entities.js";
import { fitToBounds } from "./view.js";
import { checkpoint } from "./history.js";
import { toast } from "./ui.js";
import { codeFromUnit } from "./units.js";
import { healCurrentDxf } from "./heal.js";
import { startGlbBuild, resetGlb } from "./glb.js";
import { MM_PER_UNIT, UNIT_CODE, type DxfUnit } from "../state/types.js";

// The host element (<dxf-editor>) sets itself here so ops can dispatch custom
// events on it. This is how the Rack/Stimulus host gets change notifications.
let hostElement: HTMLElement | null = null;

export function setHost(el: HTMLElement | null): void {
  hostElement = el;
}

export function emit(name: string, detail: Record<string, unknown> = {}): void {
  if (!hostElement) return;
  hostElement.dispatchEvent(
    new CustomEvent(name, { detail, bubbles: true, composed: true })
  );
}

// ------- load -------

export async function loadDxfFromFile(file: File): Promise<void> {
  const content = await file.text();
  loadDxfFromString(content, file.name);
}

export function loadDxfFromString(content: string, filename?: string): void {
  // Load brings the geometry in, normalises to origin 0,0, fits the view,
  // and checkpoints. The DXF's own $INSUNITS is applied as the editor's
  // dxfUnit default (falling back to the current setting when the file is
  // unitless), so a raw file opens with sensible units without the user
  // having to hunt for the Settings modal. The user can still override via
  // Units & Grid Settings; that override is stamped on export.
  const parsed = parseDxf(content);
  processDxf(parsed);
  store.batch(() => {
    store.write.originalDxf(content);
    store.write.filename(filename ?? null);
    store.write.customVars(parsed.customVars);
    store.write.loadedUnitCode(parsed.unitCode);
    // Apply the file's $INSUNITS as the editor default when the code maps to
    // a supported DxfUnit. Unitless / unknown codes leave the current setting
    // alone so imports of legacy files don't silently switch the editor.
    if (parsed.unitCode !== null && UNIT_CODE[parsed.unitCode]) {
      const unit = UNIT_CODE[parsed.unitCode]!;
      store.write.dxfUnit(unit);
      store.write.dxfUnitCode(parsed.unitCode);
    }
  });
  // Auto-open the Layers panel now that there's something to inspect —
  // BUT on mobile the panel is a bottom-sheet overlay that covers the canvas
  // as soon as it opens, which is intrusive when the user just wants to see
  // the drawing they loaded. Mobile users tap the Layers icon to open it.
  const mobile = store.isMobile.value;
  const sidebarWasHidden = !store.sidebarVisible();
  if (!mobile) store.write.sidebarVisible(true);
  if (!mobile && sidebarWasHidden) {
    // The panel is about to slide in and the canvas will narrow; if we call
    // fitToBounds() now it'll center against the old (wider) canvas width and
    // the drawing lands visibly off-center. Wait two frames for the panel's
    // layout + the canvas's ResizeObserver to publish the new width, then fit.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => fitToBounds());
    });
  } else {
    fitToBounds();
  }
  checkpoint("Initial Load");
  const healed = parsed.customVars["DXF_HEALED"] === "1";
  emit("dxf:load", {
    filename,
    entityCount: parsed.entities.length,
    healed,
  });
  toast(
    `Loaded${filename ? `: ${filename}` : ""} (${parsed.entities.length} entities${
      healed ? ", healed" : ""
    })`
  );

  // A brand-new file always drops the viewer back to 2D so the healing
  // overlay is visible and the user isn't staring at a stale GLB while the
  // background pipeline rebuilds. The recursive load-from-heal-response
  // call also runs this — harmless since the outer already switched.
  store.write.viewMode("2d");

  // Auto-heal + auto-GLB pipeline. When a raw (un-healed) file lands, kick
  // off /heal — its success path recursively calls loadDxfFromString with the
  // cleaned bytes, which re-enters this function with DXF_HEALED=1 and
  // proceeds straight to the GLB branch. When an already-healed file lands
  // (fresh load or the recursive heal-response call), skip heal and start
  // building the GLB in the background.
  if (healed) {
    void startGlbBuild(content);
  } else {
    void healCurrentDxf();
  }
}

// The <input type=file> dialog approach. Consumers pass the accepted files.
export function openFilePicker(onFile: (file: File) => void): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".dxf";
  input.style.display = "none";
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) onFile(file);
    input.remove();
  });
  document.body.appendChild(input);
  input.click();
}

// ------- close -------

export function closeDxf(): void {
  if (store.entities().length === 0) return;
  resetModel();
  store.clearHistory();
  store.write.filename(null);
  // Panel goes back to hidden — matches the empty-editor default state.
  store.write.sidebarVisible(false);
  // Drop the cached GLB and pop back to 2D so a re-open starts clean.
  resetGlb();
  store.write.viewMode("2d");
  emit("dxf:close");
  toast("DXF closed");
}

// ------- export -------

// Export the current model as a DXF. `outputUnit` is what the exported file
// says it is via $INSUNITS; if it differs from the in-memory dxfUnit, every
// coord is multiplied by mm(dxfUnit) / mm(outputUnit) so the file's physical
// size stays honest. Omit `outputUnit` to write in the current dxfUnit as-is.
export function exportAndDownload(filename = "exported.dxf", outputUnit?: DxfUnit): void {
  if (store.entities().length === 0) {
    toast("Nothing to export");
    return;
  }
  const dxfUnit = store.dxfUnit();
  const target: DxfUnit = outputUnit ?? dxfUnit;
  const scale = MM_PER_UNIT[dxfUnit] / MM_PER_UNIT[target];
  const targetCode = codeFromUnit(target);
  // Preserve any custom vars the file arrived with (DXF_HEALED, etc.) and
  // stamp DXF_UNIT_OVERRIDDEN=<loadedCode>:<newCode> when the export unit
  // differs from what the file said at load time — the last human decision
  // is the source of truth on the next round-trip.
  const loadedCode = store.loadedUnitCode();
  const customVars: Record<string, string> = { ...store.customVars() };
  if (loadedCode !== null && loadedCode !== targetCode) {
    customVars["DXF_UNIT_OVERRIDDEN"] = `${loadedCode}:${targetCode}`;
  }
  const content = writeDxf({
    entities: store.entities(),
    layers: store.layers(),
    unitCode: targetCode,
    scale,
    customVars,
  });
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  emit("dxf:export", { filename, size: content.length, unit: target });
  toast(`DXF exported (${target})`);
}

export function resetView(): void {
  if (!store.bounds()) {
    toast("No DXF loaded");
    return;
  }
  fitToBounds();
  toast("View reset");
}
